import React, { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import './ModelOnboarding.css';
import backIcon from '../../assets/icon/backIcon.svg';
import onboardingIcon from '../../assets/icon/onboardingIcon.svg';
import resetIcon from '../../assets/icon/resetIcon.svg';
import tickIcon from '../../assets/icon/tickIcon.svg';
import deleteIcon from '../../assets/icon/deleteIcon1.svg';
import { useAzureData } from '../../context/AzureContext';
import {
  modelOnboarding,
  modelOnboardingTrigger,
  updateModelOnboarding
} from '../../service/apiServices';
import { useToast } from '../../hooks/useToast';
import { useNavigate, useLocation } from 'react-router-dom';
import TooltipWrapper from '../../components/TooltipWrapper';
import { usePromptContext } from '../../context/PromptContextType';
import { CloudProvider } from '../../components/inputField';
import useCloudProvider from '../../hooks/useCloudProvider';

type FormData = {
  model_provider: string;
  model_type: string;
  endpoint_name: string;
  task: string;
  model_id: string;
  deployment_name: string;
  model_version: string;
  // api_key_scope: string
  input_cost_per_1k_tokens: string;
  output_cost_per_1k_tokens: string;

  keys: string;
  renewal_period: string;
  calls: string;

  pii_behaviour: string;
  safety: string;
  configuration: string;
};

type DatabricksConfig = {
  workload_size: string;
  workload_type: string;
  traffic_percentage: string;
  databricks_marketplace: string;
  min_provisioned_throughput: string;
  max_provisioned_throughput: string;
  scale_to_zero: boolean;
};

type OpenAIConfig = {
  base_url: string;
  api_version: string;
  traffic_percentage: string;
  api_key_scope: string;
};

type GeminiConfig = {
  google_region: string;
  project_id: string;
  traffic_percentage: string;
  api_key_scope: string;
};

const databricks_initialValues: DatabricksConfig = {
  workload_size: '',
  workload_type: '',
  traffic_percentage: '',
  databricks_marketplace: false,
  min_provisioned_throughput: '',
  max_provisioned_throughput: '',
  scale_to_zero: false
};

const openai_initialValues: OpenAIConfig = {
  base_url: '',
  api_version: '',
  traffic_percentage: '',
  api_key_scope: ''
};

const gemini_initialValues: GeminiConfig = {
  google_region: '',
  project_id: '',
  traffic_percentage: '',
  api_key_scope: ''
};

const initialValues: FormData = {
  model_provider: '',
  model_type: 'OpenAI',
  endpoint_name: '',
  task: 'llm/v1/chat',
  model_id: '',
  deployment_name: '',
  model_version: '',
  // api_key_scope: "",
  input_cost_per_1k_tokens: '',
  output_cost_per_1k_tokens: '',
  keys: '',
  renewal_period: '',
  calls: '',
  pii_behaviour: '',
  safety: '',
  configuration: ''
};

const redistributeTraffic = <T extends { traffic_percentage: string }>(configs: T[]): T[] => {
  if (configs.length === 1) {
    return [{ ...configs[0], traffic_percentage: '100' }];
  }
  const share = Math.floor(100 / configs.length);
  const remainder = 100 % configs.length;

  return configs.map((cfg, idx) => ({
    ...cfg,
    traffic_percentage: String(share + (idx < remainder ? 1 : 0))
  }));
};

const updateTrafficConfig = <T extends { traffic_percentage: string }>(
  configs: T[],
  setConfigs: Dispatch<SetStateAction<T[]>>,
  index: number,
  value: string
) => {
  let updated = [...configs];
  updated[index].traffic_percentage = value;

  const total = updated.reduce((sum, cfg) => sum + Number(cfg.traffic_percentage || 0), 0);
  if (total > 100) return; // prevent overshooting

  setConfigs(updated);
};

// Reusable Input Components
const TextField = ({
  label,
  name,
  value,
  required,
  disabled,
  placeholder,
  onChange,
  type,
  min,
  max
}: {
  label: string;
  name: string;
  value: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  type?: string;
  min?: number;
  max?: number;
  onChange: (key: any, value: string, min?: number) => void;
}) => (
  <Form.Group controlId={name}>
    <Form.Label>
      {label} {required && <span className="text-danger">*</span>}
    </Form.Label>
    <Form.Control
      type={!type ? 'text' : type}
      placeholder={placeholder || label}
      value={value}
      onChange={(e) => onChange(name, e.target.value, min)}
      required={required}
      disabled={disabled}
      aria-required={required}
      step="any"
      {...(type === 'number' ? { min, max } : {})}
    />
  </Form.Group>
);

const SelectField = ({
  label,
  name,
  value,
  options,
  required,
  disabled,
  onChange
}: {
  label: string;
  name: string;
  value: string;
  options: { label: string; value: string }[];
  required?: boolean;
  disabled?: boolean;
  onChange: (key: any, value: string) => void;
}) => (
  <Form.Group controlId={name}>
    <Form.Label>
      {label} {required && <span className="text-danger">*</span>}
    </Form.Label>
    <Form.Select
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
      required={required}
      disabled={disabled}
      aria-required={required}>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </Form.Select>
  </Form.Group>
);

const ToggleSwitch = ({
  value,
  onToggle,
  labels
}: {
  value: boolean;
  onToggle: () => void;
  labels: [string, string];
}) => (
  <div className="addModelToggle">
    <div className="toggle-container">
      <span className={!value ? 'label active' : 'label'}>{labels[0]}</span>
      <div className={`switch ${value ? 'public' : 'private'}`} onClick={onToggle}>
        <div className="slider-circle" />
      </div>
      <span className={value ? 'label active' : 'label'}>{labels[1]}</span>
    </div>
  </div>
);

const ModelOnboarding: React.FC = () => {
  const location = useLocation();
  const state = location.state as { editModelId?: number; model: any };
  const editModelId = state?.editModelId;
  const editModelValue = state?.model;
  const [values, setValues] = useState<FormData>(initialValues);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [databricksConfigs, setDatabricksConfigs] = useState<DatabricksConfig[]>([
    {
      workload_size: '',
      workload_type: '',
      traffic_percentage: '100',
      databricks_marketplace: false,
      min_provisioned_throughput: '',
      max_provisioned_throughput: '',
      scale_to_zero: false
    }
  ]);
  const [openaiConfigs, setOpenaiConfigs] = useState<OpenAIConfig[]>([
    {
      base_url: '',
      api_version: '',
      traffic_percentage: '100',
      api_key_scope: ''
    }
  ]);
  const [geminiConfigs, setGeminiConfigs] = useState<GeminiConfig[]>([
    {
      google_region: '',
      project_id: '',
      traffic_percentage: '100',
      api_key_scope: ''
    }
  ]);
  const { graphData } = useAzureData();
  const { apiToken } = useAzureData();
  const { showSuccess } = useToast();
  const navigate = useNavigate();
  const { modelNameDuplicationFunction } = usePromptContext();

  useEffect(() => {
    if (editModelValue && editModelId) {
      selectCloud(editModelValue?.cloud_platform_id);
      setValues({
        model_provider: editModelValue.model_provider || '',
        model_type: editModelValue.model_type || 'OpenAI',
        endpoint_name: editModelValue.endpoint_name || '',
        task: editModelValue.task || 'llm/v1/chat',
        model_id: editModelValue.model_id || '',
        deployment_name: editModelValue.deployment_name || '',
        model_version: editModelValue.model_version || '',
        // api_key_scope: editModelValue.api_key_scope || "",
        input_cost_per_1k_tokens: editModelValue.input_cost_per_1k_tokens || '',
        output_cost_per_1k_tokens: editModelValue.output_cost_per_1k_tokens || '',
        keys: editModelValue.keys || '',
        renewal_period: editModelValue.renewal_period || '',
        calls: editModelValue.calls?.toString() || '',
        pii_behaviour: editModelValue.pii_behaviour || '',
        safety: editModelValue.safety || '',
        configuration: editModelValue.configuration || ''
      });

      const modelType = editModelValue.model_type?.toLowerCase();
      if (modelType === 'databricks') {
        setDatabricksConfigs(editModelValue.databricks_configs || []);
      } else if (modelType === 'openai') {
        setOpenaiConfigs(editModelValue.openai_configs || []);
      } else if (modelType === 'gemini') {
        setGeminiConfigs(editModelValue.gemini_configs || []);
      }
    }
  }, [editModelId]);

  const update = (key: keyof FormData, value: string | boolean | number, min = 0) => {
    // Safety: prevent negative numeric values from entering state
    if (typeof value === 'number' && value < min) {
      value = min;
    }

    // Also handles numeric strings
    // console.log(value, 'fdfds', Number(value), typeof value);
    if (typeof value === 'string' && !value) {
      value = '';
    } else if (typeof value === 'string' && !isNaN(Number(value)) && Number(value) < min) {
      value = min;
    }

    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const onReset = () => {
    setValues(initialValues);
    setDatabricksConfigs([databricks_initialValues]);
    setOpenaiConfigs([openai_initialValues]);
    setGeminiConfigs([gemini_initialValues]);
  };

  const validateTraffic = () => {
    if (currentProvider === 'databricks') {
      return databricksConfigs.reduce((s, c) => s + Number(c.traffic_percentage), 0) === 100;
    }
    if (currentProvider === 'openai') {
      return openaiConfigs.reduce((s, c) => s + Number(c.traffic_percentage), 0) === 100;
    }
    if (currentProvider === 'gemini') {
      return geminiConfigs.reduce((s, c) => s + Number(c.traffic_percentage), 0) === 100;
    }
    return true;
  };
  const [nameError, setNameError] = useState(false);
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    //
    if (values?.endpoint_name && !editModelId) {
      const exists = await modelNameDuplicationFunction(values?.endpoint_name);

      if (exists) {
        setNameError(true);
        return;
      } else {
        setNameError(false);
      }
    }

    //

    if (!validateTraffic()) {
      alert('Traffic allocation must total 100%');
      return;
    }
    if (currentProvider === 'databricks') {
      const isValid =
        databricksConfigs?.length > 0 &&
        databricksConfigs.every((item) => {
          if (item.databricks_marketplace) {
            // This code make sure that when item.databricks_marketplace is true , we pass provisioned_throughput ""
            item.min_provisioned_throughput = '';
            item.max_provisioned_throughput = '';
            return true;
          }
          let min = +item.min_provisioned_throughput;
          let max = +item.max_provisioned_throughput;
          return min >= 0 && max >= 0 && min < max;
        });
      if (!isValid) {
        alert(
          'min_provisioned_throughput must be less than max_provisioned_throughput and Both values must be non-negative'
        );
        return;
      }
    }
    // setLoading(true);
    try {
      // let data = { ...values, requested_by: graphData?.mail }
      let data: any = {
        model_type: values.model_type,
        model_provider: values.model_provider,
        endpoint_name: values.endpoint_name,
        task: values.task,
        model_id: values.model_id,
        deployment_name: values.deployment_name,
        model_version: values.model_version,
        // api_key_scope: values.api_key_scope,
        input_cost_per_1k_tokens:
          values.model_type === 'Databricks' ? '0.0' : values.input_cost_per_1k_tokens,
        output_cost_per_1k_tokens:
          values.model_type === 'Databricks' ? '0.0' : values.output_cost_per_1k_tokens,
        keys: values.keys,
        renewal_period: values.renewal_period,
        calls: values.calls,
        pii_behaviour: values.pii_behaviour,
        safety: values.safety,
        // configuration: values.configuration,
        requested_by: graphData?.mail,
        cloud_platform_id: selectedCloud
      };

      // Inject only the relevant provider configs
      if (currentProvider === 'databricks') {
        data.databricks_configs = databricksConfigs;
      } else if (currentProvider === 'openai') {
        data.openai_configs = openaiConfigs;
      } else if (currentProvider === 'gemini') {
        data.gemini_configs = geminiConfigs;
      }

      if (!editModelId) {
        let response = await modelOnboarding(data, apiToken);
        if (response) {
          let response2 = await modelOnboardingTrigger(
            { cloud_platform_id: selectedCloud },
            apiToken
          );
          response2 && setSuccess(true);
          if (response2) {
            setLoading(false);
            setSuccess(true);
          }
        }
      } else {
        let response = await updateModelOnboarding(data, apiToken, editModelId);
        if (response) {
          let response2 = await modelOnboardingTrigger('', apiToken);
          response2 && setSuccess(true);
          if (response2) {
            setLoading(false);
            setSuccess(true);
          }
        }
      }
    } catch (error) {
      setLoading(false);
      setSuccess(false);
      console.error('Error: ', error);
    }
  };

  if (success && !loading) {
    showSuccess('Applied successfully!', { toastId: 'save-toast' });
    setSuccess(false);
    navigate('/model');
  }

  // ---------- Databricks ----------
  const handleAddDatabricksConfig = () => {
    setDatabricksConfigs((prev) => redistributeTraffic([...prev, { ...databricks_initialValues }]));
  };
  const handleRemoveDatabricksConfig = (index: number) => {
    setDatabricksConfigs((prev) => redistributeTraffic(prev.filter((_, i) => i !== index)));
  };

  const updateDatabricksConfig = (
    index: number,
    key: keyof DatabricksConfig,
    value: string | boolean
  ) => {
    const updated = [...databricksConfigs];

    if (!updated[index]) return;

    if (key === 'traffic_percentage') {
      updateTrafficConfig(databricksConfigs, setDatabricksConfigs, index, value as string);
      return;
    }

    // @ts-ignore
    updated[index][key] = value;
    setDatabricksConfigs(updated);
  };

  // ---------- OpenAI ----------
  const handleAddOpenaiConfig = () => {
    setOpenaiConfigs((prev) => redistributeTraffic([...prev, { ...openai_initialValues }]));
  };
  const handleRemoveOpenaiConfig = (index: number) => {
    setOpenaiConfigs((prev) => redistributeTraffic(prev.filter((_, i) => i !== index)));
  };

  function isBaseUrlPresent(arr, key, value) {
    if (!value) {
      return false;
    }
    // Count how many times the value appears
    const count = arr.filter((item) => item[key]?.trim() === value.trim()).length;
    return count >= 2;
  }

  const updateOpenaiConfig = (index: number, key: keyof OpenAIConfig, value: string | boolean) => {
    if (key === 'traffic_percentage') {
      updateTrafficConfig(openaiConfigs, setOpenaiConfigs, index, value as string);
      return;
    }

    const updated = [...openaiConfigs];

    if (!updated[index]) return;

    // @ts-ignore
    updated[index][key] = value;
    setOpenaiConfigs(updated);
  };

  // check for duplicates
  type ModelType = 'OpenAI' | 'Gemini';

  const dataBrickMArketPlaceMangement = (config: any) => {
    // This code is to manage different datatypes of databricks_marketplace in new model and edit model format
    const temp =
      typeof config.databricks_marketplace === 'string'
        ? config.databricks_marketplace === '1'
        : config.databricks_marketplace;

    return temp;
  };

  function hasDuplicatesByModel(
    model_type: ModelType,
    configs: OpenAiConfig[] | GeminiConfig[]
  ): boolean {
    const getDuplicates = (arr: any[], key: string) => {
      const counts: Record<string, number> = {};
      for (const item of arr) {
        if (item[key]) {
          counts[item[key]] = (counts[item[key]] || 0) + 1;
        }
      }
      return Object.values(counts).some((count) => count > 1);
    };

    if (model_type === 'OpenAI') {
      // Check duplicates in OpenAI configs
      const openAiConfigs = configs as OpenAiConfig[];
      if (getDuplicates(openAiConfigs, 'base_url')) return true;
      if (getDuplicates(openAiConfigs, 'api_key_scope')) return true;
    } else if (model_type === 'Gemini') {
      // Check duplicates in Gemini configs
      const geminiConfigs = configs as GeminiConfig[];
      if (getDuplicates(geminiConfigs, 'google_region')) return true;
      if (getDuplicates(geminiConfigs, 'api_key_scope')) return true;
    }

    return false; // No duplicates
  }

  const anyDuplicates = () => {
    let status = false;
    if (values.model_type === 'OpenAI') {
      status = hasDuplicatesByModel('OpenAI', openaiConfigs);
    }
    if (values.model_type === 'Gemini') {
      status = hasDuplicatesByModel('Gemini', geminiConfigs);
    }
    return status;
  };

  // ---------- Gemini ----------
  const handleAddGeminiConfig = () => {
    setGeminiConfigs((prev) => redistributeTraffic([...prev, { ...gemini_initialValues }]));
  };
  const handleRemoveGeminiConfig = (index: number) => {
    setGeminiConfigs((prev) => redistributeTraffic(prev.filter((_, i) => i !== index)));
  };

  const updateGeminiConfig = (index: number, key: keyof GeminiConfig, value: string | boolean) => {
    const updated = [...geminiConfigs];

    if (!updated[index]) return;

    if (key === 'traffic_percentage') {
      updateTrafficConfig(geminiConfigs, setGeminiConfigs, index, value as string);
      return;
    }

    // @ts-ignore
    updated[index][key] = value;
    setGeminiConfigs(updated);
  };

  // Provider-specific field maps
  const providerFields: Record<string, JSX.Element> = {
    databricks: (
      <Row
        className="g-3 model_specific_content_container"
        style={{ height: databricksConfigs.length > 1 ? '400px' : '', overflowY: 'auto' }}>
        {databricksConfigs?.map((config, index) => (
          <React.Fragment key={index}>
            <Col md={databricksConfigs.length > 1 ? 11 : 12}>
              <div
                className={`model_specific_content_box ${databricksConfigs.length > 1 && 'mb-2'}`}
                key={index}>
                <Row className="g-3">
                  <Col md={6} lg={4}>
                    <SelectField
                      label="Workload Size"
                      name="workload_size"
                      value={config.workload_size}
                      required
                      options={[
                        { label: 'Select Workload Size', value: '' },
                        { label: 'Small', value: 'Small' },
                        { label: 'Medium', value: 'Medium' },
                        { label: 'Large', value: 'Large' }
                      ]}
                      onChange={(key, val) => updateDatabricksConfig(index, key, val)}
                    />
                  </Col>
                  <Col md={6} lg={4}>
                    <SelectField
                      label="Workload Type"
                      name="workload_type"
                      value={config.workload_type}
                      required
                      options={[
                        { label: 'Select Workload Type', value: '' },
                        { label: 'Small', value: 'Small' },
                        { label: 'Medium', value: 'Medium' },
                        { label: 'Large', value: 'Large' },
                        { label: 'Large', value: 'Large' },
                        { label: 'GPU SMALL', value: 'GPU_SMALL' }
                      ]}
                      onChange={(key, val) => updateDatabricksConfig(index, key, val)}
                    />
                  </Col>
                  <Col md={6} lg={4}>
                    <TextField
                      type="number"
                      label="Traffic (%)"
                      name="traffic_percentage"
                      value={config.traffic_percentage}
                      disabled={databricksConfigs.length === 1}
                      required
                      min={0}
                      min={0}
                      onChange={(key, val) => updateDatabricksConfig(index, key, val)}
                    />
                  </Col>
                  {/* {console.log(config.databricks_marketplace, 'fdsfsdrtse')} */}
                  <Col md={6} lg={4}>
                    <Form.Group>
                      <Form.Label>Databricks Marketplace</Form.Label>
                      <ToggleSwitch
                        value={dataBrickMArketPlaceMangement(config)}
                        onToggle={() =>
                          updateDatabricksConfig(
                            index,
                            'databricks_marketplace',
                            !dataBrickMArketPlaceMangement(config)
                          )
                        }
                        labels={['No', 'Yes']}
                      />
                    </Form.Group>
                  </Col>

                  {!dataBrickMArketPlaceMangement(config) && (
                    <>
                      <Col md={6} lg={4}>
                        <TextField
                          type="number"
                          label="Min Provisioned Throughput"
                          name="min_provisioned_throughput"
                          value={config.min_provisioned_throughput}
                          required
                          onChange={(key, val) => updateDatabricksConfig(index, key, val)}
                        />
                      </Col>
                      <Col md={6} lg={4}>
                        <TextField
                          type="number"
                          label="Max Provisioned Throughput"
                          name="max_provisioned_throughput"
                          value={config.max_provisioned_throughput}
                          required
                          onChange={(key, val) => updateDatabricksConfig(index, key, val)}
                        />
                      </Col>
                    </>
                  )}
                  <Col md={12}>
                    <Form.Check
                      type="checkbox"
                      id="scale-to-zero"
                      label="Scale to zero"
                      checked={config.scale_to_zero}
                      onChange={(e) =>
                        updateDatabricksConfig(index, 'scale_to_zero', e.target.checked)
                      }
                    />
                    <div className="text-muted small mt-1">
                      Not recommended for production use. Expect higher latency on the first request
                      as the endpoint scales up.
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
            {databricksConfigs.length > 1 && (
              <Col md={1} className="d-flex">
                <button
                  type="button"
                  onClick={() => handleRemoveDatabricksConfig(index)}
                  className="btn btn-sm">
                  <img src={deleteIcon} alt="delete-bricks" />
                </button>
              </Col>
            )}
          </React.Fragment>
        ))}
      </Row>
    ),
    openai: (
      <Row
        className="g-3 model_specific_content_container"
        style={{ height: openaiConfigs.length > 1 ? '200px' : '', overflowY: 'auto' }}>
        {openaiConfigs?.map((config, index) => (
          <>
            <React.Fragment key={index}>
              <Col md={openaiConfigs.length > 1 ? 11 : 12}>
                <div className={`${databricksConfigs.length > 1 && 'mb-2'}`} key={index}>
                  <Row className="g-3">
                    <Col md={6} lg={3}>
                      <TooltipWrapper
                        id="tip-base-url"
                        tooltipText={`The base URL for the OpenAI model endpoint in a specific region.
                Example: https://bsece-ai01-p-930632-openai-03.openai.azure.com`}>
                        <TextField
                          label="Base URL"
                          name="base_url"
                          value={config.base_url}
                          required
                          onChange={(key, val) => updateOpenaiConfig(index, key, val)}
                        />
                        {isBaseUrlPresent(openaiConfigs, 'base_url', config.base_url) && (
                          <span style={{ color: 'red', fontSize: '12px' }}>
                            Duplicate Inputs not allowed
                          </span>
                        )}
                      </TooltipWrapper>
                    </Col>
                    {/* API Version */}
                    <Col md={6} lg={3}>
                      <TooltipWrapper
                        id="tip-api-version"
                        tooltipText={`The API version used for the OpenAI model.
                Example: 2024-02-15-preview`}>
                        <TextField
                          label="API Version"
                          name="api_version"
                          value={config.api_version}
                          required
                          onChange={(key, val) => updateOpenaiConfig(index, key, val)}
                        />
                      </TooltipWrapper>
                    </Col>
                    <Col md={6} lg={3}>
                      <TooltipWrapper
                        id="tip-api-key-scope"
                        tooltipText={`The Databricks secret scope and key where the API key is stored.
                          Examples: openai-key, openai-eastus2-key`}>
                        <TextField
                          label="Api key Scope"
                          name="api_key_scope"
                          value={config.api_key_scope}
                          required
                          onChange={(key, val) => updateOpenaiConfig(index, key, val)}
                        />
                        {isBaseUrlPresent(openaiConfigs, 'api_key_scope', config.api_key_scope) && (
                          <span style={{ color: 'red', fontSize: '12px' }}>
                            Duplicate Inputs not allowed
                          </span>
                        )}
                      </TooltipWrapper>
                    </Col>
                    <Col md={6} lg={3}>
                      <TextField
                        type="number"
                        label="Traffic (%)"
                        name="traffic_percentage"
                        value={config.traffic_percentage}
                        disabled={openaiConfigs.length === 1}
                        required
                        min={0}
                        onChange={(key, val) => updateOpenaiConfig(index, key, val)}
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
              {!editModelId && openaiConfigs.length > 1 && (
                <Col md={1} className="d-flex">
                  <button
                    type="button"
                    onClick={() => handleRemoveOpenaiConfig(index)}
                    className="btn btn-sm">
                    <img src={deleteIcon} alt="delete" />
                  </button>
                </Col>
              )}
            </React.Fragment>
          </>
        ))}
      </Row>
    ),
    gemini: (
      <Row
        className="g-3 model_specific_content_container"
        style={{ height: geminiConfigs.length > 1 ? '200px' : '', overflowY: 'auto' }}>
        {geminiConfigs?.map((config, index) => (
          <>
            <React.Fragment key={index}>
              <Col md={geminiConfigs.length > 1 ? 11 : 12}>
                <div className={`${geminiConfigs.length > 1 && 'mb-2'}`} key={index}>
                  <Row className="g-3">
                    <Col md={6} lg={3}>
                      <TooltipWrapper
                        id="tip-google-region"
                        tooltipText={`The region for Gemini (Vertex AI) models.
                    Example: us-central1`}>
                        <TextField
                          label="Google Region"
                          name="google_region"
                          value={config.google_region}
                          required
                          onChange={(key, val) => updateGeminiConfig(index, key, val)}
                        />
                        {isBaseUrlPresent(geminiConfigs, 'google_region', config.google_region) && (
                          <span style={{ color: 'red', fontSize: '12px' }}>
                            Duplicate Inputs not allowed
                          </span>
                        )}
                      </TooltipWrapper>
                    </Col>
                    <Col md={6} lg={3}>
                      <TooltipWrapper
                        id="tip-project-id"
                        tooltipText={`The Google Cloud project ID for Gemini models.
                  Example: ul-da-q-931039-prj`}>
                        <TextField
                          label="Project Id"
                          name="project_id"
                          value={config.project_id}
                          required
                          onChange={(key, val) => updateGeminiConfig(index, key, val)}
                        />
                      </TooltipWrapper>
                    </Col>
                    <Col md={6} lg={3}>
                      <TooltipWrapper
                        id="tip-project-id"
                        tooltipText={` The Databricks secret scope and key where the API key is stored.
                          Examples:gemini-key`}>
                        <TextField
                          label="Api key Scope"
                          name="api_key_scope"
                          value={config.api_key_scope}
                          required
                          onChange={(key, val) => updateGeminiConfig(index, key, val)}
                        />
                        {isBaseUrlPresent(geminiConfigs, 'api_key_scope', config.api_key_scope) && (
                          <span style={{ color: 'red', fontSize: '12px' }}>
                            Duplicate Inputs not allowed
                          </span>
                        )}
                      </TooltipWrapper>
                    </Col>
                    <Col md={6} lg={3}>
                      <TextField
                        type="number"
                        label="Traffic (%)"
                        name="traffic_percentage"
                        value={config.traffic_percentage}
                        disabled={geminiConfigs.length === 1}
                        required
                        min={0}
                        onChange={(key, val) => updateGeminiConfig(index, key, val)}
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
              {geminiConfigs.length > 1 && (
                <Col md={1} className="d-flex">
                  <button
                    type="button"
                    onClick={() => handleRemoveGeminiConfig(index)}
                    className="btn btn-sm">
                    <img src={deleteIcon} alt="delete-gemini" />
                  </button>
                </Col>
              )}
            </React.Fragment>
          </>
        ))}
      </Row>
    )
  };

  const currentProvider = values.model_type.trim().toLowerCase();

  const { cloudProvidersList, selectedCloud, selectCloud } = useCloudProvider();

  return (
    <div className="mt-4 ps-4 pe-4 mb-4 modal_onBoarding_parent">
      <div className="mb-4">
        <button
          onClick={() => navigate('/model')}
          className="btn btn-link text-muted p-0 mb-3 text-decoration-none back_btn">
          <img src={backIcon} alt="back" height="14px" />
          Back
        </button>

        <div className="d-flex align-items-center mb-2">
          <img src={onboardingIcon} alt="onboarding" className="me-2" />
          <h6 className="h4 mb-0 onboarding_heading">Model Onboarding</h6>
        </div>

        <p className="text-muted small mb-0 onboarding_subheading">
          Use this form to register a new AI model endpoint. You’ll need to provide model details,
          deployment configuration, and API connection settings.
        </p>
      </div>
      <Form onSubmit={onSubmit}>
        {/* General Configuration */}
        <section className="mt-3">
          <h6 className="mb-1">General Configuration</h6>
          <div className="text-muted mb-3">Basic setup of your model endpoint.</div>

          <Row className="g-3">
            <CloudProvider
              cloudProvidersList={cloudProvidersList}
              selectedCloud={selectedCloud}
              selectCloud={selectCloud}
              disabled={editModelId}
            />

            <Col md={6} lg={4}>
              <SelectField
                label="Model Type"
                name="model_type"
                value={values.model_type}
                required
                disabled={editModelId ? true : false}
                options={[
                  { label: 'Databricks', value: 'Databricks' },
                  { label: 'OpenAI', value: 'OpenAI' },
                  { label: 'Gemini', value: 'Gemini' }
                ]}
                onChange={update}
              />
            </Col>
            <Col md={6} lg={4}>
              <TooltipWrapper
                id="tip-endpoint-name"
                tooltipText={`A unique name for the serving endpoint.
            Examples: openai_v1_chat_10, gemini-25-pro, databricks-llama-4-maverick`}>
                <TextField
                  label="Endpoint Name"
                  name="endpoint_name"
                  value={values.endpoint_name}
                  required
                  onChange={update}
                  disabled={!!editModelId}
                />
                {nameError && (
                  <span style={{ color: 'red', fontSize: '12px' }}>
                    EndPoint Name already Taken
                  </span>
                )}
              </TooltipWrapper>
            </Col>
            <Col md={6} lg={4}>
              <TooltipWrapper
                id="tip-model-provider"
                tooltipText={`The name of the model provider (case-sensitive).
              Examples: openai, google_cloud_vertex_ai, databricks_dbrx, databricks_codellama_13b_python_hf`}>
                <TextField
                  label="Model Provider"
                  name="model_provider"
                  value={values.model_provider}
                  required
                  onChange={update}
                />
              </TooltipWrapper>
            </Col>
            <Col md={6} lg={4}>
              <SelectField
                label="Task"
                name="task"
                value={values.task}
                required
                options={[
                  { label: 'chat', value: 'llm/v1/chat' },
                  { label: 'embeddings', value: 'llm/v1/embeddings' },
                  { label: 'completions', value: 'llm/v1/completions' },
                  { label: 'custom_model', value: 'llm/v1/custom_model' }
                ]}
                onChange={update}
              />
            </Col>
            {/* Deployment Name */}
            <Col md={6} lg={4}>
              <TooltipWrapper
                id="tip-deployment-name"
                tooltipText={`The specific deployment name of the model from Azure AI Foundry, Vertex AI, or Databricks Marketplace.
            Examples: gpt-35-turbo-instruct, textembedding-gecko, system.ai.dbrx_instruct`}>
                <TextField
                  label="Deployment Name"
                  name="deployment_name"
                  value={values.deployment_name}
                  required
                  onChange={update}
                />
              </TooltipWrapper>
            </Col>
            {/* Model Version */}
            <Col md={6} lg={4}>
              <TooltipWrapper
                id="tip-model-version"
                tooltipText={`The version of the deployed model.
            Examples: 0914, 2`}>
                <TextField
                  label="Model Version"
                  name="model_version"
                  value={values.model_version}
                  onChange={update}
                />
              </TooltipWrapper>
            </Col>
            {currentProvider !== 'databricks' && (
              <>
                <Col md={6} lg={4}>
                  <TooltipWrapper
                    id="tip-input-cost"
                    tooltipText={`The cost for processing 1,000 input tokens for the deployed model.`}>
                    <TextField
                      type="number"
                      label="Input Cost Per 1k Tokens ($)"
                      name="input_cost_per_1k_tokens"
                      value={values.input_cost_per_1k_tokens}
                      required
                      onChange={update}
                      min={0}
                    />
                  </TooltipWrapper>
                </Col>
                {/* Output Cost */}
                <Col md={6} lg={4}>
                  <TooltipWrapper
                    id="tip-output-cost"
                    tooltipText={`The cost for generating 1,000 output tokens for the deployed model.`}>
                    <TextField
                      type="number"
                      label="Output Cost Per 1k Tokens ($)"
                      name="output_cost_per_1k_tokens"
                      value={values.output_cost_per_1k_tokens}
                      required
                      onChange={update}
                      min={0}
                    />
                  </TooltipWrapper>
                </Col>
              </>
            )}
          </Row>
        </section>

        {/* Model Specific (conditional) */}
        <section className="mt-4">
          <div className="d-flex justify-content-between">
            <div>
              <h6 className="mb-1 flex items-center">
                {`Model Specific (${values.model_type || '—'})`}
                {values.model_type?.toLowerCase() === 'databricks' && (
                  <a
                    href="https://docs.databricks.com/aws/en/machine-learning/foundation-model-apis/deploy-prov-throughput-foundation-model-apis"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="documentation-link ms-2">
                    Documentation
                  </a>
                )}
              </h6>
              <div className="text-muted mb-3">Configs unique to each model provider.</div>
            </div>
            {!editModelId && (
              <div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    if (currentProvider === 'databricks') handleAddDatabricksConfig();
                    if (currentProvider === 'openai') handleAddOpenaiConfig();
                    if (currentProvider === 'gemini') handleAddGeminiConfig();
                  }}>
                  + Add Model
                </Button>
              </div>
            )}
          </div>

          {providerFields[currentProvider] || (
            <div className="text-muted">No additional configuration required.</div>
          )}
        </section>

        {/* Rate Limit */}
        <section className="mt-4">
          <h6 className="mb-1">Rate Limit</h6>
          <div className="text-muted mb-3">Controls for usage and call limits.</div>

          <Row className="g-3">
            <Col md={6} lg={4}>
              <SelectField
                label="Keys"
                name="keys"
                value={values.keys}
                options={[
                  { label: 'Select Keys', value: '' },
                  { label: 'User', value: 'user' }
                ]}
                onChange={update}
              />
            </Col>
            <Col md={6} lg={4}>
              <SelectField
                label="Renewal Period"
                name="renewal_period"
                value={values.renewal_period}
                options={[
                  { label: 'Select Renewal Period', value: '' },
                  { label: 'Minute', value: 'minute' }
                ]}
                onChange={update}
              />
            </Col>
            <Col md={6} lg={4}>
              <TextField
                type="number"
                label="Calls"
                name="calls"
                value={values.calls}
                onChange={update}
                min={1}
              />
            </Col>
          </Row>
        </section>

        {/* Guardrails */}
        <section className="mt-4">
          <h6 className="mb-1">Guardrails</h6>
          <div className="text-muted mb-3">Rules for safe and responsible AI use.</div>

          <Row className="g-3">
            <Col md={6} lg={4}>
              <SelectField
                label="Pii Behaviour"
                name="pii_behaviour"
                value={values.pii_behaviour}
                options={[
                  { label: 'Select Pii Behaviour', value: '' },
                  { label: 'BLOCK', value: 'BLOCK' },
                  { label: 'SAFETY', value: 'SAFETY' }
                ]}
                onChange={update}
              />
            </Col>
            <Col md={6} lg={4}>
              <SelectField
                label="Safety"
                name="safety"
                value={values.safety}
                options={[
                  { label: 'Select safety', value: '' },
                  { label: 'True', value: 'true' },
                  { label: 'False', value: 'false' }
                ]}
                onChange={update}
              />
            </Col>
          </Row>
        </section>

        {/* Actions */}
        <Row className="mt-4">
          <Col className="d-flex justify-content-end gap-2">
            {!editModelId && (
              <Button className="onboarding_resetBtn" type="button" onClick={onReset}>
                <img src={resetIcon} alt="reset" className="me-1" />
                Reset Form
              </Button>
            )}
            {loading ? (
              <Button variant="primary" type="submit">
                Create <Spinner animation="border" size="sm" />
              </Button>
            ) : (
              !anyDuplicates() && (
                <Button variant="primary" type="submit">
                  <img src={tickIcon} alt="submit" className="me-1" />
                  Apply
                </Button>
              )
            )}
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default ModelOnboarding;
